import React, { useState } from "react";
import { Plus, X, Download, CheckCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input } from "@/components/Field";
import { CustomerSelect, ItemsEditor } from "@/components/admin/Editors";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

export default function Invoices() {
  const { t, sl } = useI18n();
  const { items: invoices, loading, reload: load } = useAdminData("/invoices");
  const [creating, setCreating] = useState(false);

  const markPaid = async (id, status) => { await api.put(`/invoices/${id}`, { status }); load(); };
  const download = async (inv) => {
    const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${inv.number}.pdf`; a.click(); URL.revokeObjectURL(url);
  };
  const create = async (form) => {
    try { await api.post("/invoices", form); toast.success("OK"); setCreating(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (inv) => {
    const paid = (inv.status || "").toLowerCase() === "paid";
    const msg = paid
      ? `⚠️ ACHTUNG: Rechnung ${inv.number} ist BEZAHLT.\n\nDas endgültige Löschen ist UNWIDERRUFLICH und entfernt die Rechnung dauerhaft aus dem System. Ein interner Prüfeintrag (Audit-Log) wird gespeichert.\n\nMöchten Sie diese bezahlte Rechnung wirklich unwiderruflich löschen?`
      : `Rechnung ${inv.number} wirklich löschen? Diese Aktion ist unwiderruflich.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Rechnung gelöscht"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{t("admin.invoices")}</h1>
        <button onClick={() => setCreating(true)} className="btn-primary !py-2 !px-4 text-sm" data-testid="invoice-new"><Plus size={16} /> {t("common.create")}</button>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">Nr.</th><th className="p-4">Kunde</th><th className="p-4">{t("common.total")}</th><th className="p-4">{t("common.status")}</th><th className="p-4 text-right">{t("common.actions")}</th></tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={5} /> : invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-border" data-testid={`inv-${inv.id}`}>
                <td className="p-4 font-mono">{inv.number}</td><td className="p-4">{inv.customer_name}</td>
                <td className="p-4 font-semibold">{(inv.total || 0).toFixed(2)} €</td>
                <td className="p-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${inv.status === "paid" ? "bg-green-500/15 text-green-500" : "bg-amber-500/15 text-amber-500"}`}>{sl(inv.status)}</span></td>
                <td className="p-4"><div className="flex gap-1 justify-end">
                  <button onClick={() => download(inv)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`inv-pdf-${inv.id}`}><Download size={13} /></button>
                  {inv.status !== "paid" ? <button onClick={() => markPaid(inv.id, "paid")} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`inv-paid-${inv.id}`}><CheckCircle size={13} /> Bezahlt</button>
                    : <button onClick={() => markPaid(inv.id, "unpaid")} className="btn-ghost !py-1.5 !px-3 text-xs">Offen</button>}
                  <button onClick={() => del(inv)} className="btn-ghost !py-1.5 !px-3 text-xs text-red-500 hover:text-red-600" data-testid={`inv-delete-${inv.id}`}><Trash2 size={13} /></button>
                </div></td>
              </tr>
            ))}
            {!loading && invoices.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {creating && <InvoiceDrawer onClose={() => setCreating(false)} onSave={create} />}
    </div>
  );
}

function InvoiceDrawer({ onClose, onSave }) {
  const { t } = useI18n();
  const [f, setF] = useState({ customer_id: "", customer_name: "", discount: 0, vat_rate: 0 });
  const [items, setItems] = useState([{ name: "", qty: 1, price: 0 }]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={(e) => { e.preventDefault(); onSave({ ...f, items }); }} className="relative w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4" data-testid="invoice-drawer">
        <div className="flex items-center justify-between"><h2 className="font-display text-xl font-black">{t("admin.invoices")}</h2><button type="button" onClick={onClose}><X size={20} /></button></div>
        <Field label="Kunde"><CustomerSelect testid="inv-customer" value={f.customer_id} onChange={(id, name) => setF({ ...f, customer_id: id, customer_name: name })} /></Field>
        <div><span className="block text-sm font-medium mb-1.5">Positionen</span><ItemsEditor items={items} setItems={setItems} /></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rabatt €"><Input testid="inv-discount" type="number" value={f.discount} onChange={(e) => setF({ ...f, discount: Number(e.target.value) })} /></Field>
          <p className="text-xs text-muted-foreground">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>
        </div>
        <button className="btn-primary w-full" data-testid="invoice-save">{t("common.create")}</button>
      </form>
    </div>
  );
}
